import React, { useEffect, useMemo, useState } from "react";
import useAxios from "../utils/useAxios";
import { FaDownload, FaSearch, FaMoneyBillWave, FaHandHoldingUsd, FaBalanceScale } from 'react-icons/fa';
import { useSidebar } from '../context/SidebarContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/RHStatsPage';

const API_URL = "/pointage/stats-global/";

function formatMoney(x) {
  const n = Number(x || 0);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function formatHours(x) {
  const n = Number(x || 0);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

export default function RHStatsPage() {
  const api = useAxios();
  const { sidebarVisible } = useSidebar();

  const moisOptions = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const currentYear = new Date().getFullYear();

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [mois, setMois] = useState(defaultMonth);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // États pour la gestion des organisations
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);

 
  const loadData = async (orgId = selectedOrganisation) => {
    if (!orgId) return; 
    
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (mois) params.set("mois", mois);
      if (orgId) params.set("organisation", orgId);
      
      const url = `${API_URL}?${params.toString()}`;
      const res = await api.get(url);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  
  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    
    loadData(orgId);
  };

  useEffect(() => {
    if (selectedOrganisation) {
      loadData();
    }
  }, [mois, selectedOrganisation]);

  const filteredEmployees = useMemo(() => {
    if (!data?.employes) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.employes;
    return data.employes.filter((e) => {
      const nom = (e.nom || "").toLowerCase();
      const prenom = (e.prenom || "").toLowerCase();
      const poste = (e.poste || "").toLowerCase();
      return nom.includes(q) || prenom.includes(q) || poste.includes(q);
    });
  }, [data, search]);

  const stats = useMemo(() => {
    if (!filteredEmployees.length) return {
      totalPrimes: 0,
      totalHeuresSup: 0,
      totalPenalites: data?.penalites?.montant_total || 0,
      soldeGlobal: -(data?.penalites?.montant_total || 0)
    };

    const totalPrimes = filteredEmployees.reduce((sum, e) => sum + (e.montant_prime || 0), 0);
    const totalHeuresSup = filteredEmployees.reduce((sum, e) => sum + (e.montant_heures_supplementaires || 0), 0);
    const totalPenalites = data?.penalites?.montant_total || 0;
    const soldeGlobal = (totalPrimes + totalHeuresSup) - totalPenalites;

    return {
      totalPrimes,
      totalHeuresSup,
      totalPenalites,
      soldeGlobal
    };
  }, [filteredEmployees, data]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`Statistiques RH - ${currentOrganisation?.nom || ''} - ${moisOptions[parseInt(mois.split('-')[1]) - 1]} ${mois.split('-')[0]}`, 14, 20);

    doc.setFontSize(12);
    doc.text(`Période: ${data?.periode?.debut} → ${data?.periode?.fin}`, 14, 30);
    doc.text(`Total primes: ${formatMoney(stats.totalPrimes)} FCFA`, 14, 40);
    doc.text(`Total heures sup: ${formatMoney(stats.totalHeuresSup)} FCFA`, 14, 50);
    doc.text(`Total pénalités: ${formatMoney(stats.totalPenalites)} FCFA`, 14, 60);
    doc.text(`Solde global: ${formatMoney(stats.soldeGlobal)} FCFA`, 14, 70);

    const tableColumn = ["ID", "Nom", "Prénom", "Poste", "Prime", "Heures Sup", "Montant HS", "Total Rémunération", "Heures travaillées"];
    const tableRows = filteredEmployees.map(e => [
      e.employe_id || e.id,
      e.nom,
      e.prenom,
      e.poste,
      formatMoney(e.montant_prime),
      formatHours(e.heures_supplementaires),
      formatMoney(e.montant_heures_supplementaires),
      formatMoney(e.total_remuneration),
      formatHours(e.heures_travaillees)
    ]);

    autoTable(doc, { 
      head: [tableColumn], 
      body: tableRows, 
      startY: 80, 
      styles: { fontSize: 10 }, 
      headStyles: { fillColor: [27, 1, 155] } 
    });

    doc.save(`stats_rh_${currentOrganisation?.nom || ''}_${mois}.pdf`);
  };

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      <h3 style={styles.header}>Statistiques globales primes penalites</h3>
      <hr style={styles.hr} />

      {/* Header avec sélecteur d'organisation */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />

        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Statistiques pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          {/* Filtres */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Mois :</label>
              <select
                value={mois}
                onChange={(e) => setMois(e.target.value)}
                style={styles.select}
              >
                {moisOptions.map((mois, index) => {
                  const moisValue = `${currentYear}-${String(index + 1).padStart(2, '0')}`;
                  return (
                    <option key={moisValue} value={moisValue}>
                      {mois} {currentYear}
                    </option>
                  );
                })}
              </select>
            </div>

            <div style={styles.filterGroup}>
              <FaSearch style={styles.filterIcon} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher nom, prénom ..."
                style={styles.searchBar}
              />
            </div>

            <button
              onClick={handleExportPDF}
              disabled={loading}
              style={styles.exportButton}
            >
              <FaDownload /> Exporter PDF
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div style={{ ...styles.card, ...styles.positiveCard }}>
                <FaMoneyBillWave size={24} />
                <h5 className="mt-2" style={{ fontWeight: '700' }}>
                  {formatMoney(stats.totalPrimes + stats.totalHeuresSup)} FCFA
                </h5>
                <p className="mb-0">Total Primes + Heures Sup</p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div style={{ ...styles.card, ...styles.negativeCard }}>
                <FaHandHoldingUsd size={24} />
                <h5 className="mt-2" style={{ fontWeight: '700' }}>
                  {formatMoney(stats.totalPenalites)} FCFA
                </h5>
                <p className="mb-0">Total Pénalités</p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div style={{ 
                ...styles.card, 
                ...(stats.soldeGlobal >= 0 ? styles.positiveCard : styles.negativeCard) 
              }}>
                <FaBalanceScale size={24} />
                <h5 className="mt-2" style={{ fontWeight: '700' }}>
                  {formatMoney(Math.abs(stats.soldeGlobal))} FCFA
                </h5>
                <p className="mb-0">
                  {stats.soldeGlobal >= 0 ? 'Rémunération Nette' : 'Sanctions Net'}
                </p>
              </div>
            </div>
          </div>

          <div className="alert alert-info mb-4" style={{ borderRadius: '15px' }}>
            <strong>Période :</strong> {data?.periode?.debut} → {data?.periode?.fin}
          </div>

          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body" style={{ overflowX: "auto" }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Détail par employé ({filteredEmployees.length} résultat{filteredEmployees.length !== 1 ? 's' : ''})
              </div>
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th>Poste</th>
                    <th>Prime ponctualité</th>
                    <th>Heures sup (h)</th>
                    <th>Montant HS</th>
                    <th>Total rémunération</th>
                    <th>Heures travaillées</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((e) => (
                    <tr key={e.employe_id || e.id}>
                      <td>{e.nom}</td>
                      <td>{e.prenom}</td>
                      <td>{e.poste}</td>
                      <td style={{ color: '#28a745', fontWeight: '600' }}>{formatMoney(e.montant_prime)} FCFA</td>
                      <td>{formatHours(e.heures_supplementaires)}</td>
                      <td style={{ color: '#28a745', fontWeight: '600' }}>{formatMoney(e.montant_heures_supplementaires)} FCFA</td>
                      <td style={{ color: '#28a745', fontWeight: '600' }}>{formatMoney(e.total_remuneration)} FCFA</td>
                      <td>{formatHours(e.heures_travaillees)}</td>
                    </tr>
                  ))}
                  {!loading && filteredEmployees.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-muted py-4">
                        Aucun résultat
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {loading && <div className="text-center py-3">Chargement...</div>}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
}